import { isMissingAuth } from '@takeshape/schema';
import { value } from '@takeshape/util';
import isString from 'lodash/isString.js';
import { branding } from "../../config.js";
import { addAuthenticationFormConfig, addAuthenticationSchema, authenticationFormConfig, updateAuthentication } from "../../util/authentication.js";
import { hasValidEndpoint } from "../../util/config.js";
import { addHealthCheckFormConfig, addHealthCheckSchema, getHealthCheck, healthCheckFormConfig, updateHealthCheck } from "../../util/health.js";
import { getInitialData } from "../../util/initial-data.js";
const serviceProvider = {
    name: 'rest',
    title: 'REST',
    description: `Adding a generic REST service connection allows you to create new queries and mutations for your ${branding.productName} API that utilize the external REST service.`,
    links: [
        {
            title: 'Read More',
            url: 'https://en.wikipedia.org/wiki/Representational_state_transfer'
        },
        { title: 'Docs', url: '/docs/services/providers/rest' }
    ],
    serviceType: 'rest',
    createId: value('rest'),
    getFormSchema: value(addAuthenticationSchema(addHealthCheckSchema({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '^[0-9a-z_-]+$'
            },
            title: {
                title: 'Name',
                type: 'string'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$'
            },
            endpoint: {
                title: 'Endpoint',
                type: 'string',
                pattern: '^https?:\\/\\/'
            }
        },
        required: ['title', 'id', 'namespace', 'endpoint']
    }, { checkNames: ['ping', 'none'] }))),
    getFormConfig: value(addHealthCheckFormConfig(addAuthenticationFormConfig({
        order: ['title', 'id', 'namespace', 'endpoint'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique, url-safe identifier for this service.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            endpoint: {
                widget: 'singleLineText'
            }
        }
    }))),
    getForms: value({
        ...healthCheckFormConfig,
        ...authenticationFormConfig
    }),
    getInitialData(serviceConfig) {
        const initialData = getInitialData(serviceProvider, serviceConfig);
        if (initialData.isNew) {
            initialData.healthCheck = {
                checkName: 'none'
            };
            initialData.authentication = {
                type: 'none'
            };
        }
        return initialData;
    },
    getConfig(formData) {
        const config = {
            title: formData.title ?? 'REST',
            namespace: formData.namespace ?? 'REST',
            provider: 'rest',
            serviceType: 'rest',
            authenticationType: 'none'
        };
        if (formData?.endpoint && isString(formData.endpoint)) {
            config.options = {
                endpoint: formData.endpoint
            };
        }
        return updateHealthCheck(updateAuthentication(config, formData), formData);
    },
    isCompleteConfig(serviceConfig) {
        if (isMissingAuth(serviceConfig)) {
            return false;
        }
        return hasValidEndpoint(serviceConfig);
    },
    getHealthCheck: getHealthCheck()
};
export default serviceProvider;
